import type { Notification } from '../../types/notifications'

export type PriorityKind = 'urgent' | 'approval' | 'followUp' | 'general'

// Work assigned to the viewer themselves (an assessment, a document, a path...). The
// backend lists these as dynamic items that always report `pending`, so a past deadline
// has to be read from the date itself.
const OWN_WORK_TYPES = new Set(['assessment_pending', 'content_assignment_pending'])

export const isOwnWork = (notification: Pick<Notification, 'type'>) =>
  OWN_WORK_TYPES.has(notification.type.toLowerCase())

const localDateKey = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

const isPastDue = (notification: Pick<Notification, 'deadline' | 'is_completed'>, today: Date) =>
  !notification.is_completed && !!notification.deadline && notification.deadline.slice(0, 10) < localDateKey(today)

export const getPriorityKind = (
  notification: Pick<Notification, 'type' | 'status' | 'deadline' | 'is_completed'>,
  today = new Date(),
): PriorityKind => {
  const type = notification.type.toLowerCase()
  if (notification.status === 'late' || notification.status === 'expired') return 'urgent'
  if (isOwnWork(notification) && isPastDue(notification, today)) return 'urgent'
  if (type === 'leave_request_submitted' || type === 'schedule_request_submitted') return 'approval'
  if (
    type.includes('checkin') ||
    type.includes('contract') ||
    type.includes('probation') ||
    type.includes('profile') ||
    type.includes('user') ||
    type === 'attendance_empty_this_week'
  ) return 'followUp'
  return 'general'
}

const RANKS: Record<PriorityKind, number> = { urgent: 0, approval: 1, followUp: 2, general: 3 }

export const comparePriorities = (a: Notification, b: Notification, today = new Date()) => {
  const byKind = RANKS[getPriorityKind(a, today)] - RANKS[getPriorityKind(b, today)]
  if (byKind) return byKind
  // Within the same urgency, the viewer's own work comes before other people's requests.
  const byOwner = Number(isOwnWork(b)) - Number(isOwnWork(a))
  if (byOwner) return byOwner
  const deadline = (item: Notification) => {
    const value = item.deadline ? Date.parse(item.deadline) : NaN
    return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER
  }
  return deadline(a) - deadline(b)
}

/**
 * The few items the home card has room for. An admin can have dozens of approvals
 * waiting, which used to fill every slot and hide the viewer's own assessments and
 * assignments; the most pressing piece of their own work always keeps a place.
 */
export const pickShownNotifications = (sorted: Notification[], limit: number) => {
  const shown = sorted.slice(0, limit)
  if (limit < 1 || shown.some(isOwnWork)) return shown
  const firstOwn = sorted.find(isOwnWork)
  return firstOwn ? [...shown.slice(0, limit - 1), firstOwn] : shown
}
